import { useCallback, useMemo, useState } from 'react';
import type { Option } from '~/styles/forms/SearchSelect';

export type Person = { id: number; name: string };

/**
 * Legacy's member typeahead is a paged select2: `minimumInputLength: 3`,
 * `delay: 250` and `per_page: 20` (`assets/js/member-tagging.js:22-45`,
 * `class-group-member-tagging.php:88-116`). Exported so the caller's query and
 * the control's own "keep typing" hint cannot drift apart.
 */
export const PEOPLE_PICKER_MIN_QUERY = 3;
export const PEOPLE_PICKER_PAGE_SIZE = 20;
export const PEOPLE_PICKER_DEBOUNCE_MS = 250;

/**
 * Search results supplied by the caller instead of filtered in the hook, so the
 * request can be a real `useQuery` living where its `group_id` does. The picker
 * still owns the selection: `onSearch` fires on every keystroke and the caller
 * decides (at `PEOPLE_PICKER_MIN_QUERY`) whether that becomes a request.
 */
export interface PeopleSearchSource {
  results: Person[];
  loading: boolean;
  onSearch: (query: string) => void;
}

const toOption = (p: Person): Option => ({
  value: String(p.id),
  label: p.name,
});

export function useAsyncPeoplePicker(
  source: Person[] | PeopleSearchSource,
  multi: boolean,
) {
  const [value, setValue] = useState<string[]>([]);
  const [selectedPool, setSelectedPool] = useState<Person[]>([]);
  const [localResults, setLocalResults] = useState<Person[]>([]);
  const [localLoading, setLocalLoading] = useState(false);

  // Two narrowings rather than one boolean flag: the mock branch needs `source`
  // typed as an array and the served branch needs it typed as the search source.
  const served = Array.isArray(source) ? null : source;
  const localSource = Array.isArray(source) ? source : null;

  const results = served ? served.results : localResults;
  const loading = served ? served.loading : localLoading;

  const options = useMemo<Option[]>(() => {
    const map = new Map<string, Option>();
    for (const p of [...selectedPool, ...results]) {
      map.set(String(p.id), toOption(p));
    }
    return Array.from(map.values());
  }, [selectedPool, results]);

  const onSearch = (query: string) => {
    if (served) {
      served.onSearch(query);
      return;
    }
    if (query.length < PEOPLE_PICKER_MIN_QUERY) {
      setLocalResults([]);
      setLocalLoading(false);
      return;
    }
    setLocalLoading(true);
    // Client-side stand-in for callers still on mock data.
    const lower = query.toLowerCase();
    const timer = setTimeout(() => {
      setLocalResults(
        (localSource ?? []).filter((p) => p.name.toLowerCase().includes(lower)),
      );
      setLocalLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  };

  const onChange = (next: string[]) => {
    setValue(next);
    const poolById = new Map(selectedPool.map((p) => [String(p.id), p]));
    const resultsById = new Map(results.map((p) => [String(p.id), p]));
    const merged = next
      .map((id) => poolById.get(id) ?? resultsById.get(id))
      .filter((p): p is Person => p != null);
    setSelectedPool(merged);
  };

  const preselect = useCallback(
    (person: Person) => {
      setSelectedPool((prev) =>
        prev.some((p) => p.id === person.id) ? prev : [...prev, person],
      );
      setValue((prev) => {
        const id = String(person.id);
        if (prev.includes(id)) return prev;
        return multi ? [...prev, id] : [id];
      });
    },
    [multi],
  );

  return { value, options, loading, onSearch, onChange, preselect };
}
